import type React from 'react';
import { useEffect, useState } from 'react';
import type { PercentCrop } from 'react-image-crop';

type UseDrawCroppedImageToCanvasArgs = {
  imageUrl: string;
  cropInfo: PercentCrop;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  width: number;
  height: number;
};

export const useDrawCroppedImageToCanvas = (args: UseDrawCroppedImageToCanvasArgs) => {
  const { imageUrl, cropInfo, canvasRef, width, height } = args;

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Canvas buffer can't be drawn at zero dimensions — skip until layout is measured
    if (width === 0 || height === 0) return;

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Allow cross-origin images
    image.src = imageUrl;

    image.onload = () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Set canvas size to match the desired output size
          const devicePixelRatio = window.devicePixelRatio;

          try {
            const { x, y, width: cropWidth, height: cropHeight } = cropInfo;

            // Convert percent values to pixels
            const cropX = (x / 100) * image.width;
            const cropY = (y / 100) * image.height;
            const cropWidthPx = (cropWidth / 100) * image.width;
            const cropHeightPx = (cropHeight / 100) * image.height;

            canvas.width = width * devicePixelRatio;
            canvas.height = height * devicePixelRatio;
            ctx.scale(devicePixelRatio, devicePixelRatio);

            ctx.imageSmoothingQuality = 'high';

            // Clear canvas and draw the cropped image
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(
              image,
              cropX,
              cropY,
              cropWidthPx,
              cropHeightPx, // Source crop
              0,
              0,
              width,
              height, // Destination canvas
            );
            setHasError(false);
          } catch (error) {
            setHasError(true);
            // Fallback to showing the full image
            canvas.width = width * devicePixelRatio;
            canvas.height = height * devicePixelRatio;
            ctx.scale(devicePixelRatio, devicePixelRatio);
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(image, 0, 0, width, height);
          }
        }
      }
    };

    image.onerror = () => {
      if (cancelled) return;
      setHasError(true);
    };

    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [cropInfo, width, height, imageUrl, canvasRef]);

  return {
    hasError,
  };
};
